import { KeyboardEvent, useCallback } from 'react'
import { useIsMutating } from 'react-query'
import {
  Box,
  Step as MuiStep,
  stepConnectorClasses,
  StepIconProps,
  StepLabel,
  stepLabelClasses,
  Stepper as MuiStepper,
  styled,
  SxProps,
  Theme,
} from '@mui/material'
import clsx from 'clsx'
import { findIndex } from 'lodash'

import { Page } from 'common/interfaces'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { useFlow } from 'utils/flow'
import { getFlowSteps, getSteps, RenderedStep } from 'utils/getSteps'
import { useCurrentPage } from 'utils/hook'

const srOnly: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
}

const styles: Record<string, SxProps<Theme>> = {
  box: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  stepper: {
    overflowY: 'auto',
    [`& .${stepConnectorClasses.root}`]: {
      marginLeft: '10px',
    },
    [`& .${stepConnectorClasses.lineVertical}`]: {
      minHeight: '32px',
      borderLeftStyle: 'dashed',
      borderColor: '#B4B4B4',
    },
  },
  step: {
    [`& .${stepLabelClasses.root}`]: {
      padding: 0,
    },
    [`&.${stepLabelClasses.completed}`]: {
      cursor: 'pointer',
      [`& .${stepLabelClasses.label}`]: {
        fontWeight: 400,
      },
      [`& + .${stepConnectorClasses.root} .${stepConnectorClasses.lineVertical}`]: {
        borderLeftStyle: 'solid',
      },
    },
    [`&:not(.${stepLabelClasses.completed})`]: {
      cursor: 'not-allowed',
    },
    [`& .${stepLabelClasses.disabled}`]: {
      cursor: 'not-allowed',
    },
    [`& .${stepLabelClasses.iconContainer}`]: {
      paddingRight: '12px',
    },
    [`& .${stepLabelClasses.label}.${stepLabelClasses.active}`]: {
      fontWeight: 600,
    },
    '&:focus-visible': {
      outline: (theme: Theme) => `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: '2px',
      borderRadius: '4px',
    },
  },
}

type StepIconCustomProps = StepIconProps & {
  isFinalStep: boolean
}

const StepIconWrapper = styled('span')<Pick<StepIconCustomProps, 'completed' | 'isFinalStep'>>(
  ({ theme, completed, isFinalStep }) => ({
    width: '20px',
    height: '20px',
    padding: '0 6px',
    border: `2px solid ${completed ? theme.palette.success.light : '#BDBDBD'}`,
    borderRadius: '50%',
    backgroundColor: completed ? theme.palette.success.light : 'white',
    ...(completed && {
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L4 7L10 1' stroke='white' stroke-width='2'/%3E%3C/svg%3E%0A")`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }),
    ...(!isFinalStep && {
      '&.circle-icon--active': {
        borderColor: theme.palette.primary.main,
      },
    }),
  })
)

const StepIcon = ({ isFinalStep, active, completed }: StepIconCustomProps): JSX.Element => (
  <StepIconWrapper
    className={clsx('circle-icon', { 'circle-icon--active': isFinalStep || active })}
    completed={completed! || isFinalStep}
    isFinalStep={isFinalStep}
  />
)

export default function Stepper(): JSX.Element {
  const { box, stepper, step } = styles
  const { data: flow } = useFlow()
  const { pages } = flow!
  const currentPage = useCurrentPage()
  const { longest_path_length: longestPathLength } = currentPage
  const { isSubmitted, path } = useSessionState()
  const [dispatch] = useSessionDispatch()
  const isMutatingNumber = useIsMutating()
  const isMutating = !(isMutatingNumber === 0)

  // When the flow defines an explicit step list, always show exactly those
  // steps; otherwise fall back to the path-derived (visited + lookahead) steps.
  // stepper_steps is a backend JSONField, so guard against any non-array value.
  const stepperSteps = Array.isArray(flow!.stepper_steps) ? flow!.stepper_steps : []

  let renderedSteps: RenderedStep[]
  let activeStep: number

  if (stepperSteps.length > 0) {
    renderedSteps = getFlowSteps(stepperSteps, path, currentPage, pages)
    // Stays -1 when the current page isn't one of the configured steps (e.g. an
    // error page) — no step is marked active. Completed steps are still rendered
    // via each step's explicit `completed` prop, so the connector line is correct.
    activeStep = findIndex(renderedSteps, (renderStep) => renderStep.isCurrent)
  } else {
    const pathSteps = getSteps([...path, currentPage.id], pages)
    activeStep = findIndex(pathSteps, { name: currentPage.name })
    renderedSteps = pathSteps.map((pathStep, index) => ({
      name: pathStep.name,
      label: pathStep.title,
      navigateId: path.includes(pathStep.id) ? pathStep.id : null,
      isCurrent: index === activeStep,
      isCompleted: index < activeStep,
    }))
  }

  const goToStep = useCallback(
    (id: Page['id']) => dispatch({ type: 'goTo', step: id }),
    [dispatch]
  )

  const handleKeyDown = useCallback(
    (id: Page['id']) => (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        goToStep(id)
      }
    },
    [goToStep]
  )

  return (
    <Box component="nav" aria-label="Progress" className="main-sidebar-stepper" sx={box}>
      <MuiStepper orientation="vertical" sx={stepper} activeStep={activeStep}>
        {renderedSteps.map(({ name, label, navigateId, isCompleted, isCurrent }, index) => {
          const isClickable = navigateId !== null && !isSubmitted && !isMutating

          return (
            <MuiStep
              {...(isClickable && {
                onClick: () => goToStep(navigateId),
                onKeyDown: handleKeyDown(navigateId),
                tabIndex: 0,
                role: 'button',
              })}
              sx={step}
              completed={isCompleted}
              disabled={isMutating || isSubmitted}
              key={`${name}-${index}`}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <StepLabel
                StepIconComponent={(props) => (
                  <StepIcon isFinalStep={longestPathLength === 0} {...props} />
                )}
              >
                {label}
                <Box component="span" sx={srOnly}>
                  {isCompleted ? ' - completed' : isCurrent ? ' - current step' : ' - not completed'}
                </Box>
              </StepLabel>
            </MuiStep>
          )
        })}
      </MuiStepper>
    </Box>
  )
}
